import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  CheckCircle2,
  MessageSquare,
  Tag,
  HelpCircle,
  Reply,
  X,
} from 'lucide-react';
import { useStore } from '../store/useStore';
import { formatDateTime, generateId } from '../lib/utils';
import { RoleBadge } from '../components/Badge';
import type { ChatMessage } from '../types';

function ThreadGroup({
  messages,
  threadId,
  users,
  stages,
  onResolve,
  onReply,
}: {
  messages: ChatMessage[];
  threadId: string;
  users: ReturnType<typeof useStore.getState>['users'];
  stages: ReturnType<typeof useStore.getState>['stages'];
  onResolve: (threadId: string) => void;
  onReply: (threadId: string) => void;
}) {
  const firstMsg = messages[0];
  const isResolved = messages.every((m) => m.resolved);
  const linkedStage = stages.find((s) => s.id === firstMsg.relatedStageId);
  const hasQuestion = messages.some((m) => m.isQuestion);

  return (
    <div className={`rounded-xl border overflow-hidden ${isResolved ? 'border-green-100 opacity-75' : hasQuestion ? 'border-purple-200' : 'border-gray-100'}`}>
      {/* Thread header */}
      <div className={`px-5 py-3 flex items-center justify-between ${isResolved ? 'bg-green-50' : hasQuestion ? 'bg-purple-50' : 'bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          {hasQuestion ? (
            <HelpCircle size={14} className={isResolved ? 'text-green-500' : 'text-purple-500'} />
          ) : (
            <MessageSquare size={14} className="text-gray-400" />
          )}
          <span className="text-xs font-medium text-gray-600">
            {isResolved ? 'Resolved thread' : hasQuestion ? 'Open question' : 'Discussion thread'}
          </span>
          {linkedStage && (
            <span className="flex items-center gap-1 text-xs text-gray-400 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
              <Tag size={10} /> {linkedStage.title}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isResolved && (
            <button
              onClick={() => onReply(threadId)}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 bg-white border border-gray-200 px-2.5 py-1 rounded-lg font-medium transition-colors"
            >
              <Reply size={12} /> Reply
            </button>
          )}
          {!isResolved && hasQuestion && (
            <button
              onClick={() => onResolve(threadId)}
              className="flex items-center gap-1.5 text-xs text-green-600 hover:text-green-800 bg-white border border-green-200 px-2.5 py-1 rounded-lg font-medium transition-colors"
            >
              <CheckCircle2 size={12} /> Mark Resolved
            </button>
          )}
          {isResolved && (
            <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
              <CheckCircle2 size={12} /> Resolved
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="bg-white divide-y divide-gray-50">
        {messages.map((msg, idx) => {
          const sender = users.find((u) => u.id === msg.senderId);
          const isFirst = idx === 0;
          return (
            <div key={msg.id} className={`px-5 py-4 flex gap-3 ${msg.isQuestion && isFirst ? 'bg-purple-50/30' : ''}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 mt-0.5 ${
                sender?.role === 'pm' ? 'bg-teal-500' :
                sender?.role === 'sales' ? 'bg-blue-500' :
                sender?.role === 'sales_pm' ? 'bg-indigo-500' :
                'bg-purple-500'
              }`}>
                {sender?.initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-gray-900">{sender?.name}</span>
                  <RoleBadge role={sender?.role ?? ''} />
                  <span className="text-xs text-gray-400">{formatDateTime(msg.createdAt)}</span>
                  {msg.isQuestion && (
                    <span className="text-xs text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded font-medium">Question</span>
                  )}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{msg.message}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Chat() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projects = useStore((s) => s.projects);
  const clients = useStore((s) => s.clients);
  const users = useStore((s) => s.users);
  const stages = useStore((s) => s.stages);
  const messages = useStore((s) => s.messages);
  const addMessage = useStore((s) => s.addMessage);
  const resolveThread = useStore((s) => s.resolveThread);
  const fetchMessages = useStore((s) => s.fetchMessages);
  const currentUser = useStore((s) => s.currentUser);

  // Messages are lazy-loaded per project (they're not part of the main hydrate).
  useEffect(() => {
    if (projectId) fetchMessages(projectId);
  }, [projectId, fetchMessages]);

  const [newMessage, setNewMessage] = useState('');
  const [isQuestion, setIsQuestion] = useState(false);
  const [selectedStageId, setSelectedStageId] = useState('');
  const [filter, setFilter] = useState<'all' | 'open' | 'resolved'>('all');
  const [replyingToThreadId, setReplyingToThreadId] = useState<string | null>(null);

  const project = projects.find((p) => p.id === projectId);
  const client = clients.find((c) => c.id === project?.clientId);
  const projectMessages = messages.filter((m) => m.projectId === projectId);
  const projectStages = stages.filter((s) => s.projectId === projectId).sort((a, b) => a.order - b.order);

  // Group messages by threadId
  const threads = new Map<string, ChatMessage[]>();
  projectMessages.forEach((msg) => {
    if (!threads.has(msg.threadId)) threads.set(msg.threadId, []);
    threads.get(msg.threadId)!.push(msg);
  });

  const sortedThreads = Array.from(threads.entries())
    .map(([threadId, msgs]) => ({
      threadId,
      msgs: msgs.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
      isResolved: msgs.every((m) => m.resolved),
      isQuestion: msgs.some((m) => m.isQuestion),
    }))
    .sort((a, b) => {
      const aTime = new Date(a.msgs[a.msgs.length - 1].createdAt).getTime();
      const bTime = new Date(b.msgs[b.msgs.length - 1].createdAt).getTime();
      return bTime - aTime;
    });

  const filteredThreads = sortedThreads.filter((t) => {
    if (filter === 'open') return !t.isResolved;
    if (filter === 'resolved') return t.isResolved;
    return true;
  });

  const openCount = sortedThreads.filter((t) => !t.isResolved && t.isQuestion).length;

  const handleSend = () => {
    if (!newMessage.trim()) return;
    const threadId = replyingToThreadId ?? generateId();
    addMessage({
      projectId: projectId!,
      relatedStageId: selectedStageId || null,
      senderId: currentUser!.id,
      message: newMessage.trim(),
      isQuestion: replyingToThreadId ? false : isQuestion,
      resolved: false,
      threadId,
    });
    setNewMessage('');
    setIsQuestion(false);
    setSelectedStageId('');
    setReplyingToThreadId(null);
  };

  const handleReply = (threadId: string) => {
    setReplyingToThreadId(threadId);
    setIsQuestion(false);
    // Scroll compose into view
    document.getElementById('chat-compose')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  if (!project) {
    return <div className="p-8 text-center text-gray-400">Project not found.</div>;
  }

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <button onClick={() => navigate(`/projects/${projectId}`)} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 mb-6 transition-colors">
        <ArrowLeft size={14} /> {project.name}
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Q&A / Clarifications</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            {client?.companyName} · {project.name}
            {openCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                {openCount} open
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Compose area */}
      <div id="chat-compose" className="bg-white rounded-xl border border-gray-100 p-5 mb-6">
        {replyingToThreadId && (
          <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mb-3">
            <span className="flex items-center gap-1.5 text-xs text-blue-700 font-medium">
              <Reply size={12} /> Replying to thread
            </span>
            <button
              onClick={() => setReplyingToThreadId(null)}
              className="text-blue-400 hover:text-blue-700"
            >
              <X size={13} />
            </button>
          </div>
        )}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-bold">
            {currentUser?.initials}
          </div>
          <span className="text-sm font-medium text-gray-700">{currentUser?.name}</span>
        </div>
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend(); }}
          placeholder="Ask a question or add a comment about this project..."
          rows={3}
          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
        />
        <div className="flex items-center justify-between mt-3 gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            {!replyingToThreadId && (
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isQuestion}
                  onChange={(e) => setIsQuestion(e.target.checked)}
                  className="rounded text-brand-500"
                />
                <span className="text-xs text-gray-600 font-medium">Mark as question</span>
              </label>
            )}
            {projectStages.length > 0 && !replyingToThreadId && (
              <select
                value={selectedStageId}
                onChange={(e) => setSelectedStageId(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <option value="">No stage tag</option>
                {projectStages.map((s) => (
                  <option key={s.id} value={s.id}>{s.title}</option>
                ))}
              </select>
            )}
          </div>
          <button
            onClick={handleSend}
            disabled={!newMessage.trim()}
            className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 text-white text-sm rounded-lg hover:bg-brand-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send size={13} /> Send
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">Ctrl+Enter to send</p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 bg-white border border-gray-100 rounded-lg p-1 mb-5">
        {[
          { id: 'all', label: 'All threads' },
          { id: 'open', label: `Open (${openCount})` },
          { id: 'resolved', label: 'Resolved' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id as typeof filter)}
            className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-colors ${
              filter === tab.id ? 'bg-brand-500 text-white' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Threads */}
      <div className="space-y-4">
        {filteredThreads.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <MessageSquare size={32} className="mx-auto mb-3 text-gray-200" />
            <p className="text-gray-400 text-sm">No messages yet. Start a conversation above.</p>
          </div>
        )}
        {filteredThreads.map(({ threadId, msgs }) => (
          <ThreadGroup
            key={threadId}
            messages={msgs}
            threadId={threadId}
            users={users}
            stages={projectStages}
            onResolve={resolveThread}
            onReply={handleReply}
          />
        ))}
      </div>
    </div>
  );
}
